import type { HTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import MdiPlus from '~icons/mdi/plus'
import { openMembershipMoreCreditsModal } from 'src/components/Membership/MembershipMoreCreditsModal'
import { useMyInfo } from 'src/services/apollo/auth'
import { useMyQuota } from 'src/services/apollo/quota'
import { cx } from 'src/utils/cx'
import { track } from 'src/utils/tracker'
import CreditMeter from './CreditMeter'

type Variant = 'purple-pink' | 'monochrome' | 'navigation'

const classes = {
  'purple-pink': {
    wrapper: cx(
      'ring-violet-600 hover:ring-violet-700 bg-background-secondary pl-1 py-[2px] rounded-lg p-[2px]',
    ),
    meter: cx('[&>span>svg>path]:fill-[url(#member-purple-pink-gradient)]'),
  },
  monochrome: {
    wrapper: cx('ring-neutral-300 hover:ring-neutral-600 rounded p-px'),
    meter: cx('hover:bg-black/10 dark:hover:bg-white/10'),
  },
  navigation: {
    wrapper: cx(
      'ring-neutral-200/80 hover:ring-neutral-300 dark:hover:ring-neutral-600 bg-background-secondary/70 dark:bg-white/[0.06] rounded-lg px-1 py-[2px]',
    ),
    meter: cx(
      'text-purple-600 dark:text-purple-400 text-xs sm:text-sm font-medium',
    ),
  },
} as Record<Variant, Record<'wrapper' | 'meter', string>>

type MyCreditProps = HTMLAttributes<HTMLElement> & {
  variant?: Variant
  hideTopUpButtonOnMsm?: boolean
  truncateOnMobile?: boolean
}

export default function MyCredits({
  className,
  style,
  onClick,
  truncateOnMobile = false,
  variant = 'monochrome',
  hideTopUpButtonOnMsm = false,
  ...props
}: MyCreditProps) {
  const { t } = useTranslation(['membership'])
  const { data: me } = useMyInfo()
  const { data: credits, loading } = useMyQuota()

  const readonly = !me

  return (
    <span
      className={cx(
        'inline-flex gap-px hover:ring-1 group',
        classes[variant].wrapper,
        className,
      )}
      style={style}
    >
      <CreditMeter
        truncateOnMobile={truncateOnMobile}
        as={Link}
        to={me ? `/@${me.username}/credits` : ''}
        target="_blank"
        value={credits}
        updating={loading}
        className={cx(
          'flex-none h-6 px-1 flex items-center no-underline  rounded-[3px] transition',
          classes[variant].meter,
          readonly && 'pointer-events-none',
        )}
        {...props}
        onClick={(...args) => {
          track('credit_balance_click')
          onClick?.(...args)
        }}
      />
      {!readonly && variant === 'monochrome' && (
        <button
          className={cx(
            'flex-none w-6 h-6 -ml-1 flex justify-center items-center hover:bg-theme-primary text-neutral-500 dark:text-neutral-400 hover:!text-white rounded-[3px] transition',
            hideTopUpButtonOnMsm && 'max-msm:hidden',
          )}
          onClick={() => {
            openMembershipMoreCreditsModal({
              scene: 'gen-plan',
              trackingLocation: 'gen-plan',
              defaultMode: 'packs',
            })
          }}
        >
          <MdiPlus className="w-5 h-5" />
        </button>
      )}
      {!readonly && variant === 'purple-pink' && (
        <button
          className={cx(
            'flex justify-center items-center bg-gradient-purple-pink text-white text-xs font-medium px-1 mlg:px-3 rounded-md transition',
            hideTopUpButtonOnMsm && 'max-msm:hidden',
          )}
          onClick={() => {
            openMembershipMoreCreditsModal({
              scene: 'gen-plan',
              trackingLocation: 'gen-plan',
              defaultMode: 'packs',
            })
          }}
        >
          <span className="hidden mlg:inline">
            {t('membership:membership.top-up.label')}
          </span>
          <MdiPlus className="size-4 inline mlg:hidden" />
        </button>
      )}
    </span>
  )
}
